/**
 * Maps site-level typed preferences into Coverage Matrix setup rows and
 * cross-links environmental prefs to document attestation fields.
 */
import type {
  EnvironmentalPreferences,
  ProjectIntent,
  TypedPreferencesByType,
} from '../../types/siteRag';
import type { SiteContext } from '../../types/siteContext';
import type { SiteEntity } from '../../types/site';
import type { ReportTypeId } from '../../config/reportTypes';
import type { RequirementAutoFill, RequirementAutoFillRegistry } from './compileCoverageAutoFill';
import {
  SITE_PROJECT_INTENT_ID,
  SETUP_ENABLED_REPORT_TYPES,
  reportSetupRequirementId,
  isSetupEnabledReportType,
} from '../../config/reportSetupRequirements';
import { getDocumentChecklistFor } from '../../config/documentChecklists';
import {
  resolveReportSetupFields,
  setupFormValuesToPreferences,
} from './resolveReportSetupFields';
import {
  mapEnvironmentalPrefsToCoverage,
  environmentalPrefsToRequirementAutoFill,
} from './environmentalPrefsCoverageFill';

function prefFill(
  requirementId: string,
  partial: Pick<
    RequirementAutoFill,
    'fields' | 'notes' | 'fieldsFilledCount' | 'fieldsTotalCount'
  >,
): RequirementAutoFill {
  return {
    requirementId,
    ...partial,
    confidence: {
      tier: 'medium',
      value: 0.6,
      rationale: 'Site report setup config',
    },
    sourceProvider: 'typed-preferences',
    sourceUrl: '',
  };
}

function setupRowAutoFill(
  reportType: ReportTypeId,
  typedPreferences: TypedPreferencesByType | undefined | null,
  projectIntent: ProjectIntent | undefined | null,
  siteContext: SiteContext | undefined | null,
  site?: SiteEntity | null,
): RequirementAutoFill | null {
  const requirementId = reportSetupRequirementId(reportType);
  const item = getDocumentChecklistFor(reportType).find(i => i.id === requirementId);
  if (!item) return null;

  const resolved = resolveReportSetupFields({
    requirement: item,
    reportType,
    projectIntent,
    siteContext,
    typedPreferences,
    site,
  });
  if (resolved.structuredFilled === 0) return null;

  const fields: Record<string, string | number> = {};
  for (const f of resolved.primary) {
    if (f.isEmpty || f.value === undefined) continue;
    if (typeof f.value === 'boolean') fields[f.key] = f.value ? 1 : 0;
    else if (typeof f.value === 'number' || typeof f.value === 'string') fields[f.key] = f.value;
  }

  return prefFill(requirementId, {
    fields,
    notes: `Pre-filled from site ${reportType} report setup.`,
    fieldsFilledCount: resolved.structuredFilled,
    fieldsTotalCount: resolved.structuredTotal,
  });
}

function siteIntentAutoFill(
  reportType: ReportTypeId,
  projectIntent: ProjectIntent | undefined | null,
  siteContext: SiteContext | undefined | null,
  typedPreferences: TypedPreferencesByType | undefined | null,
): RequirementAutoFill | null {
  const item = getDocumentChecklistFor(reportType).find(i => i.id === SITE_PROJECT_INTENT_ID);
  if (!item) return null;

  const resolved = resolveReportSetupFields({
    requirement: item,
    reportType,
    projectIntent,
    siteContext,
    typedPreferences,
  });
  if (resolved.structuredFilled === 0) return null;

  const fields: Record<string, string | number> = {};
  for (const f of resolved.primary) {
    if (f.isEmpty || f.value === undefined) continue;
    if (typeof f.value === 'number' || typeof f.value === 'string') fields[f.key] = f.value;
  }

  return prefFill(SITE_PROJECT_INTENT_ID, {
    fields,
    notes: 'Pre-filled from Site Project Intent.',
    fieldsFilledCount: resolved.structuredFilled,
    fieldsTotalCount: resolved.structuredTotal,
  });
}

/** Map typed prefs + intent into setup requirement auto-fill entries. */
export function typedPreferencesToRequirementAutoFill(args: {
  typedPreferences?: TypedPreferencesByType | null;
  projectIntent?: ProjectIntent | null;
  siteContext?: SiteContext | null;
  site?: SiteEntity | null;
  reportTypes?: readonly ReportTypeId[];
}): RequirementAutoFillRegistry {
  const registry: RequirementAutoFillRegistry = {};
  const types = args.reportTypes ?? SETUP_ENABLED_REPORT_TYPES;

  for (const reportType of types) {
    if (!isSetupEnabledReportType(reportType)) continue;
    const intentFill = siteIntentAutoFill(
      reportType,
      args.projectIntent,
      args.siteContext,
      args.typedPreferences,
    );
    if (intentFill) registry[intentFill.requirementId] = intentFill;

    const setupFill = setupRowAutoFill(
      reportType,
      args.typedPreferences,
      args.projectIntent,
      args.siteContext,
      args.site,
    );
    if (setupFill) registry[setupFill.requirementId] = setupFill;
  }

  const envPrefs = args.typedPreferences?.environmental;
  if (envPrefs) {
    const envRegistry = environmentalPrefsToRequirementAutoFill(envPrefs);
    for (const [id, fill] of Object.entries(envRegistry)) {
      const existing = registry[id];
      if (existing) {
        registry[id] = {
          ...existing,
          fields: { ...fill.fields, ...existing.fields },
          notes: [fill.notes, existing.notes].filter(Boolean).join(' '),
          fieldsFilledCount: Object.keys({ ...fill.fields, ...existing.fields }).length,
          fieldsTotalCount: Math.max(fill.fieldsTotalCount, existing.fieldsTotalCount),
        };
      } else {
        registry[id] = fill;
      }
    }
  }

  return registry;
}

export { mapEnvironmentalPrefsToCoverage, environmentalPrefsToRequirementAutoFill };
